import type { AbonoInput, CalcularVentaInput, CalculoVentaDTO, DocumentoDTO, DocumentoResumenDTO, EsperaDTO, EsperaInput, PagoDocumentoEntrada, PagoDTO, VentaEntrada } from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { api, del, get, nuevaClaveOperacion, post } from '@/lib/api.ts';

export interface FiltrosDocumentos {
  estado?: string | undefined;
  q?: string | undefined;
  tipo?: string | undefined;
  estado_pago?: string | undefined;
  estado_entrega?: string | undefined;
  cliente_id?: string | undefined;
  desde?: string | undefined;
  hasta?: string | undefined;
  pagina?: number | undefined;
  limite?: number | undefined;
}
export interface PaginaDocumentos {
  datos: DocumentoResumenDTO[];
  total: number;
  pagina: number;
  limite: number;
}

export const clavesVentas = {
  lista: (f: FiltrosDocumentos) => ['ventas', 'lista', f] as const,
  detalle: (id: string) => ['ventas', 'detalle', id] as const,
  esperas: ['ventas', 'esperas'] as const,
};

export const useDocumentos = (f: FiltrosDocumentos) => useQuery({ queryKey: clavesVentas.lista(f), queryFn: () => get<PaginaDocumentos>('/ventas', f as Record<string, string | number | undefined>), placeholderData: (p) => p });
export const useDocumento = (id: string | null) => useQuery({ queryKey: clavesVentas.detalle(id ?? ''), queryFn: () => get<DocumentoDTO>(`/ventas/${id}`), enabled: !!id });
export const useEsperas = (habilitado = true) => useQuery({ queryKey: clavesVentas.esperas, queryFn: () => get<EsperaDTO[]>('/ventas/espera'), enabled: habilitado });

function useInvalidarVentas() {
  const qc = useQueryClient();
  return (id?: string) => Promise.all([
    qc.invalidateQueries({ queryKey: ['ventas'] }),
    qc.invalidateQueries({ queryKey: ['productos'] }),
    qc.invalidateQueries({ queryKey: ['clientes'] }),
    // H-13: la referencia de Zelle/Binance que se acaba de usar deja de verse como disponible
    qc.invalidateQueries({ queryKey: ['pagos-esperados'] }),
    qc.invalidateQueries({ queryKey: ['cotizaciones'] }),
    qc.invalidateQueries({ queryKey: ['inventario'] }),
    // Cada cobro puede dejar la caja sobre el límite de efectivo: se relee su estado (aviso de retiro parcial)
    qc.invalidateQueries({ queryKey: ['caja'] }),
    id ? qc.invalidateQueries({ queryKey: clavesVentas.detalle(id) }) : Promise.resolve(),
  ]);
}

export const calcularVenta = (d: CalcularVentaInput) => post<CalculoVentaDTO>('/ventas/calcular', d);

export function useEmitirVenta() {
  const inv = useInvalidarVentas();
  return useMutation({
    mutationFn: ({ datos, clave }: { datos: VentaEntrada; clave: string }) => api<DocumentoDTO>('POST', '/ventas', { body: datos, headers: { 'idempotency-key': clave } }),
    onSuccess: () => inv(),
  });
}
export function usePagarDocumento(id: string) {
  const inv = useInvalidarVentas();
  return useMutation({
    mutationFn: ({ datos, clave }: { datos: PagoDocumentoEntrada; clave: string }) => api<DocumentoDTO>('POST', `/ventas/${id}/pagos`, { body: datos, headers: { 'idempotency-key': clave } }),
    onSuccess: () => inv(id),
  });
}
export function useAbono() {
  const inv = useInvalidarVentas();
  return useMutation({
    mutationFn: ({ datos, clave }: { datos: AbonoInput; clave: string }) => api<PagoDTO & { cliente_id: string; saldo_a_favor: string }>('POST', '/ventas/abonos', { body: datos, headers: { 'idempotency-key': clave } }),
    onSuccess: () => inv(),
  });
}
export function useGuardarEspera() {
  const qc = useQueryClient();
  // Clave de operación: el doble clic en «F9 · Espera» guarda una sola (hallazgo T22); se renueva al guardar
  const clave = useRef(nuevaClaveOperacion());
  return useMutation({
    mutationFn: (d: EsperaInput) => post<EsperaDTO>('/ventas/espera', d, { headers: { 'idempotency-key': clave.current } }),
    onSuccess: () => {
      clave.current = nuevaClaveOperacion();
      void qc.invalidateQueries({ queryKey: clavesVentas.esperas });
    },
  });
}
export function useDescartarEspera() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => del<{ ok: boolean }>(`/ventas/espera/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: clavesVentas.esperas }) });
}
/** Hoja dañada: anula número y control y reemite con los siguientes (el documento conserva líneas, cobros y entregas). */
export function useReemitir(id: string) {
  const inv = useInvalidarVentas();
  return useMutation({
    // numero_actual: el número que muestra la pantalla; si otra ya lo reemitió, el servidor rechaza (documento_ya_reemitido)
    mutationFn: ({ motivo, autorizaciones, clave, numero_actual }: { motivo: string; autorizaciones: string[]; clave: string; numero_actual: string }) =>
      api<DocumentoDTO>('POST', `/ventas/${id}/reemitir`, { body: { motivo, autorizaciones, numero_actual }, headers: { 'idempotency-key': clave } }),
    onSuccess: () => inv(id),
    onError: () => inv(id), // el documento pudo cambiar en otra pantalla: se vuelve a leer
  });
}
export interface ControlAnulado { id: string; control_formateado: string; motivo: string; usuario_nombre: string; anulado_en: string }
export const useControlesAnulados = (habilitado: boolean) => useQuery({ queryKey: ['ventas', 'controles-anulados'], queryFn: () => get<ControlAnulado[]>('/ventas/controles/anulados'), enabled: habilitado });
// La clave de operación evita que un doble clic anule dos hojas (hallazgo T13)
export const anularControl = (motivo: string, autorizaciones: string[], clave: string) => post<ControlAnulado>('/ventas/controles/anular', { motivo, autorizaciones }, { headers: { 'idempotency-key': clave } });
export const registrarImpresion = (id: string) => post<{ impresiones: number; copia: boolean }>(`/ventas/${id}/impresion`);
